'use client';

import { useCallback, useEffect, useState } from 'react';
import { fetchAccountDetails } from '@/services/microservices/account';
import { ensureIdentityGrantSession, LegacySessionExpiredError } from '@/services/microservices/authenticate';
import { getMicroservicesCountryCode } from '@/services/microservices/usedCarEligibility';
import { getClientSdk } from './clientSdk';
import { SnapshotAuthState } from './SnapshotUser.types';
import {
    getAuthenticatedUserRestoreContext,
    type SnapshotAuthenticatedUser,
    type TeslaManageSelectionData,
} from './snapshotSignInFlowServices';

interface DefaultBillingProfileResponse {
    billingProfileName?: {
        firstAndLastName?: {
            firstName?: string;
            lastName?: string;
        };
    };
}

interface AccountResponse {
    accountNumber?: string | null;
}

const SNAPSHOT_SIGNED_OUT_KEY = 'snapshot-user-signed-out';

interface UseSnapshotAuthResult {
    authState: SnapshotAuthState;
    isRestoringAuth: boolean;
    authenticatedUser: SnapshotAuthenticatedUser | null;
    teslaManageSelection: TeslaManageSelectionData | undefined;
    handleAuthenticated: (user: SnapshotAuthenticatedUser) => void;
    handleSignOut: () => void;
}

export const useSnapshotAuth = (): UseSnapshotAuthResult => {
    const [authState, setAuthState] = useState<SnapshotAuthState>('anonymous');
    const [isRestoringAuth, setIsRestoringAuth] = useState(true);
    const [authenticatedUser, setAuthenticatedUser] = useState<SnapshotAuthenticatedUser | null>(null);
    const [teslaManageSelection, setTeslaManageSelection] = useState<TeslaManageSelectionData | undefined>();

    useEffect(() => {
        let cancelled = false;

        const restoreAuthenticatedUser = async () => {
            const signedOutLocally = window.sessionStorage.getItem(SNAPSHOT_SIGNED_OUT_KEY) === 'true';

            const clientSdk = await getClientSdk();

            if (!clientSdk || cancelled) {
                if (!cancelled) {
                    setAuthState('anonymous');
                    setAuthenticatedUser(null);
                    setIsRestoringAuth(false);
                }
                return;
            }

            const userInfo = clientSdk.user.getUserInfo();

            // A stale local sign-out marker should not override a real restored
            // browser session from the Client SDK on another host or tab.
            if (signedOutLocally && userInfo.session?.sessionType === 'authenticated') {
                window.sessionStorage.removeItem(SNAPSHOT_SIGNED_OUT_KEY);
            }

            if (signedOutLocally && userInfo.session?.sessionType !== 'authenticated') {
                if (!cancelled) {
                    setAuthState('anonymous');
                    setAuthenticatedUser(null);
                    setIsRestoringAuth(false);
                }
                return;
            }

            if (userInfo.session?.sessionType !== 'authenticated') {
                if (!cancelled) {
                    setAuthState('anonymous');
                    setAuthenticatedUser(null);
                    setIsRestoringAuth(false);
                }
                return;
            }

            if (!cancelled) {
                setAuthState('authenticated');
                setAuthenticatedUser({
                    accountNumber: '',
                    firstName: '',
                    userName: userInfo.handle || 'My Account',
                });
            }

            try {
                const restoreContext = await getAuthenticatedUserRestoreContext(clientSdk).catch(() => undefined);
                const accountResponse = (await clientSdk.user.account()) as AccountResponse;
                const accountNumber = accountResponse.accountNumber || '';
                const countryCode = getMicroservicesCountryCode();

                if (restoreContext?.requiresSmsLegacyLogin) {
                    try {
                        await ensureIdentityGrantSession(clientSdk, countryCode, {
                            allowExistingLegacySession: true,
                        });
                    } catch (error) {
                        if (error instanceof LegacySessionExpiredError) {
                            await clientSdk.user.signOut().catch(() => undefined);

                            if (!cancelled) {
                                setAuthenticatedUser(null);
                                setTeslaManageSelection(undefined);
                                setAuthState('anonymous');
                            }

                            return;
                        }

                        throw error;
                    }
                }

                const accountDetailsResponse = accountNumber
                    ? await fetchAccountDetails(countryCode, {
                          accountNumber,
                          identifiedUser: true,
                      }).catch(() => undefined)
                    : undefined;

                // The default billing profile can legitimately 404 — accounts
                // can exist without one (fresh streaming trials, lightly-
                // provisioned test accounts). Don't let that throw away the
                // account info we just successfully fetched.
                let firstName = accountDetailsResponse?.data?.firstName?.trim() || '';
                try {
                    const billingProfileResponse =
                        (await clientSdk.commerce.billing.getDefaultBillingProfile()) as DefaultBillingProfileResponse;
                    firstName =
                        firstName ||
                        billingProfileResponse.billingProfileName?.firstAndLastName?.firstName?.trim() ||
                        '';
                } catch (billingError) {
                    console.warn(
                        '[snapshot-auth] No billing profile available; falling back to handle for userName',
                        billingError,
                    );
                }

                const userName = firstName || userInfo.handle || '';

                if (!cancelled) {
                    setAuthenticatedUser({ accountNumber, accountDetailsResponse, firstName, userName });
                    setTeslaManageSelection(restoreContext?.teslaManageSelection);
                    setAuthState('authenticated');
                }
            } catch (error) {
                console.warn('[snapshot-auth] Failed to load account', error);
            } finally {
                if (!cancelled) {
                    setIsRestoringAuth(false);
                }
            }
        };

        void restoreAuthenticatedUser();

        return () => {
            cancelled = true;
        };
    }, []);

    const handleAuthenticated = useCallback((user: SnapshotAuthenticatedUser) => {
        window.sessionStorage.removeItem(SNAPSHOT_SIGNED_OUT_KEY);
        setAuthenticatedUser(user);
        setTeslaManageSelection(undefined);
        setAuthState('authenticated');
        setIsRestoringAuth(false);
    }, []);

    const handleSignOut = useCallback(() => {
        window.sessionStorage.setItem(SNAPSHOT_SIGNED_OUT_KEY, 'true');
        setAuthenticatedUser(null);
        setTeslaManageSelection(undefined);
        setAuthState('anonymous');
        setIsRestoringAuth(false);
    }, []);

    return {
        authState,
        isRestoringAuth,
        authenticatedUser,
        teslaManageSelection,
        handleAuthenticated,
        handleSignOut,
    };
};
